import { MessageWithCategoryAndType } from "../../utils/product/getPromoMessaging";

type StringFormatTagsResponse = string[];

const numberTags = (messages: MessageWithCategoryAndType[]) => {
  const numberedTagsObj = messages.reduce(
    (acc: Record<string, number>, message: MessageWithCategoryAndType) => {
      const { tag } = message;
      if (tag) {
        if (acc[tag]) {
          acc[tag] = (acc[tag] as number) + 1;
        } else {
          acc[tag] = 1;
        }
      }
      return acc;
    },
    {},
  );

  return numberedTagsObj;
};

const stringFormatTags = (
  numberedTagsObj: Record<string, number>,
  category: "offers" | "claims",
): StringFormatTagsResponse => {
  const tagWithQuantityArray = Object.entries(numberedTagsObj);

  const stringFormatTag = (tagAndNumber: [string, number]) => {
    if (tagAndNumber[1] === 1) {
      return `${tagAndNumber[0]}`;
    } else {
      return `${tagAndNumber[0]} (${tagAndNumber[1]})`;
    }
  };

  if (tagWithQuantityArray.length > 4) {
    const totalRemainingMessages = tagWithQuantityArray
      .slice(3)
      .reduce((acc, tagWithQuantity) => acc + tagWithQuantity[1], 0);

    return [
      stringFormatTag(tagWithQuantityArray[0] as [string, number]),
      stringFormatTag(tagWithQuantityArray[1] as [string, number]),
      stringFormatTag(tagWithQuantityArray[2] as [string, number]),
      `+${totalRemainingMessages} more`,
    ];
  }

  if (tagWithQuantityArray.length > 2) {
    const totalRemainingMessages = tagWithQuantityArray
      .slice(1)
      .reduce((acc, tagWithQuantity) => acc + tagWithQuantity[1], 0);

    return [
      stringFormatTag(tagWithQuantityArray[0] as [string, number]),
      `+${totalRemainingMessages} more ${category}`,
    ];
  } else {
    return tagWithQuantityArray.map((tagAndNumber) =>
      stringFormatTag(tagAndNumber),
    );
  }
};

export const formatTagsForDisplay = (
  messages: MessageWithCategoryAndType[],
) => {
  const offerMessages = messages.filter(
    (message) =>
      message.category === "offers" || message.category === "services",
  );
  const claimMessages = messages.filter(
    (message) => message.category === "claims",
  );

  const numberedTags = numberTags([...offerMessages, ...claimMessages]);
  const formattedTags = {
    offers: stringFormatTags(numberedTags, "offers"),
    claims: [],
  };
  return formattedTags;
};

export const getPromoTitleFromDescription = (
  title: string,
  description: string,
): string => {
  const beforeClaimRegex = /^(.*?)<p>\s*<strong>\s*How to claim/;
  const match = description.match(beforeClaimRegex);

  const descriptionStartsWithTitle = doesDescriptionAndTitleStartWithSameText(
    description,
    title,
  );

  if (!match) {
    return title;
  }

  return descriptionStartsWithTitle && match && match[1] ? match[1] : title;
};

export const stripHTML = (str: string): string => {
  return str.replace(/<[^>]*>/g, "").trim();
};

export const normalizeHTML = (str: string) =>
  str
    .replace(/>\s+</g, "><") // Remove spaces between tags
    .replace(/\s+/g, " ") // Normalize all spaces
    .trim();

export const normalizeAndStripHTML = (str: string): string =>
  stripHTML(str)
    .replace(/\s+/g, " ") // Normalize all whitespaces to a single space
    .replace(/^\s+|\s+$/g, "") // Remove leading and trailing spaces
    .toLowerCase(); // Case-insensitive comparison

const getPrefixMatchLength = (str1: string, str2: string): number => {
  const words1 = str1.split(/\s+/);
  const words2 = str2.split(/\s+/);
  let matchLength = 0;

  for (let i = 0; i < Math.min(words1.length, words2.length); i++) {
    if (words1[i] === words2[i]) {
      matchLength++;
    } else {
      break;
    }
  }

  return matchLength;
};

export const doesDescriptionAndTitleStartWithSameText = (
  description: string,
  title: string,
): boolean => {
  const strippedTitle = normalizeAndStripHTML(title);
  const strippedDescription = normalizeAndStripHTML(description);

  // Get how many words match from the start of both strings
  const matchingLength = getPrefixMatchLength(
    strippedTitle,
    strippedDescription,
  );

  // If the start of the description matches the start of the title (at least 3 words for now), return true
  return matchingLength >= 3;
};

export const getDescriptionStartingWithClaim = (
  description: string,
  title: string,
): string => {
  const claimRegex = /<p>\s*<strong>\s*How to claim[\s\S]*<\/p>/i;
  const matchesHowToClaim = description.match(claimRegex);

  const descriptionStartsWithTitle = doesDescriptionAndTitleStartWithSameText(
    description,
    title,
  );

  // Escape the title for regex
  const escapedTitle = normalizeHTML(title)
    .replace(/[.*+?^${}()|[\]\\]/g, "\\$&") // Escape metacharacters
    .replace(/\s+/g, "\\s*"); // Allow optional spaces within the title

  // Regex to match variations of the title in the HTML
  const removeTitleFromDescription = new RegExp(
    `<p>\\s*<strong>\\s*${escapedTitle}[.,!?\\s]*</strong>\\s*</p>|<strong>\\s*${escapedTitle}[.,!?\\s]*</strong>|<p>\\s*${escapedTitle}[.,!?\\s]*</p>|${escapedTitle}[.,!?\\s]*`,
    "gi",
  );

  // If description starts with the title, return the "How to claim" section
  if (descriptionStartsWithTitle && matchesHowToClaim && matchesHowToClaim[0]) {
    return matchesHowToClaim[0];
  }

  // Otherwise, remove the title from the description and return the remaining content
  return normalizeHTML(description)
    .replace(removeTitleFromDescription, "")
    .trim();
};
